import * as React from 'react';
import { useSelector } from 'react-redux';
import { Outlet, Route, Routes } from 'react-router-dom';
import ProtectedRoute from 'auth/ProtectedRoute';
import { isInsuranceEnabled } from 'config/insurance';
import { isViewerLaunchPlaygroundEnabled } from 'config/playground/viewerLaunchPlayground';
import PageNotFound from 'pages/PageNotFound';
import SelectNewApplicationPage from 'pages/SelectNewApplicationPage';
import StatusPage from 'pages/StatusPage';
import { RoommateSelectionConfirmationProvider } from 'providers/RoommateSelectionConfirmationProvider';
import { RootState } from 'state/store';

import PdfSigner from 'components/documents/PdfSigner';
import PdfViewer from 'components/documents/PdfViewer';
import { Loader } from 'components/Loader';

const RedirectToForm = React.lazy(() => import('pages/RedirectToForm'));
const LeasingJourney = React.lazy(() => import('pages/LeasingJourney'));
const LeadProfile = React.lazy(() => import('pages/lead-profile/LeadProfile'));
const EntryPage = React.lazy(() => import('pages/entry-page/EntryPage'));
const RedirectToAuth = React.lazy(() => import('pages/RedirectToAuth'));
const ThankYouPage = React.lazy(() => import('pages/ThankYouPage'));
const AuthenticationScreen = React.lazy(() => import('auth/AuthenticationScreen'));
const PreviewApplication = React.lazy(() => import('pages/preview-application/PreviewApplication'));
const PreviewRoute = React.lazy(() => import('pages/preview-route/PreviewRoute'));
const EntrataProspectLogin = React.lazy(() => import('auth/EntrataProspectLogin'));
const ScreeningConditionsPage = React.lazy(
  () => import('components/render-page/pages/screening-conditions/ScreeningConditionsPage'),
);
const ScreeningConditionPayment = React.lazy(
  () => import('components/render-page/pages/screening-conditions/ScreeningConditionPayment'),
);
const ScreeningConditionConfirmation = React.lazy(
  () => import('components/render-page/pages/screening-conditions/ScreeningConditionConfirmation'),
);
const ProspectResetPassword = React.lazy(() => import('pages/ResetPasswordForm'));
const SignupEmbed = React.lazy(() => import('auth/SignupEmbed'));
const ViewerLaunchPlayground = React.lazy(() => import('pages/dev/ViewerLaunchPlayground'));
const InsuranceEnrollmentPage = React.lazy(() => import('pages/insurance/InsuranceEnrollmentPage'));

// Preload components to avoid lazy loading issues
import('pages/LeasingJourney');

function App() {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const insuranceEnabled = isInsuranceEnabled();

  const withSuspense = (Component: React.LazyExoticComponent<any>) => (
    <React.Suspense fallback={<Loader />}>
      <Component />
    </React.Suspense>
  );

  return (
    <RoommateSelectionConfirmationProvider>
      <Routes>
        {isViewerLaunchPlaygroundEnabled() && (
          <Route path="/dev/viewer-launch" element={withSuspense(ViewerLaunchPlayground)} />
        )}
        <Route path="preview/:versionId" element={withSuspense(PreviewApplication)} />
        <Route path="previewroute/:applicationId/:versionId" element={withSuspense(PreviewRoute)} />
        <Route
          path="/lead-profile/verify-session/:orgId/:propertyId/entrata-lead-profile-login"
          element={withSuspense(LeadProfile)}
        />
        <Route
          path="/:orgId/:propertyId/reset-password"
          element={withSuspense(ProspectResetPassword)}
        />
        <Route
          path="/:orgId/:propertyId/entrata-prospect-login"
          element={withSuspense(EntrataProspectLogin)}
        />
        <Route
          path="/:orgId/:propertyId/viewer-login"
          element={withSuspense(AuthenticationScreen)}
        />
        <Route path="/:orgId/:propertyId" element={withSuspense(AuthenticationScreen)} />
        <Route path="/:orgId/:propertyId/signup" element={withSuspense(SignupEmbed)} />

        {isAuthenticated ? (
          <>
            <Route
              path="/:orgId/:propertyId/insurance-enrollment"
              element={insuranceEnabled ? withSuspense(InsuranceEnrollmentPage) : <PageNotFound />}
            />
            <Route path="/:orgId/:propertyId/:pageId" element={withSuspense(LeasingJourney)} />
            <Route path="/:orgId/:propertyId/entry" element={withSuspense(EntryPage)} />
            <Route
              path="/:orgId/:propertyId/screening"
              element={withSuspense(ScreeningConditionsPage)}
            />
            <Route
              path="/:orgId/:propertyId/new-application"
              element={<SelectNewApplicationPage />}
            />
            <Route
              path="/:orgId/:propertyId/screening/payment"
              element={withSuspense(ScreeningConditionPayment)}
            />
            <Route
              path="/:orgId/:propertyId/screening/confirmation"
              element={withSuspense(ScreeningConditionConfirmation)}
            />
            <Route path="/:orgId/:propertyId/viewPdf/:pageId" element={<PdfViewer />} />
            <Route path="/:orgId/:propertyId/signPdf/:pageId" element={<PdfSigner />} />
            <Route
              path="/:orgId/:propertyId/submission-confirmation"
              element={withSuspense(ThankYouPage)}
            />
            <Route path="/status/:status" element={<StatusPage />} />

            <Route
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route element={withSuspense(RedirectToForm)} />
            </Route>
          </>
        ) : (
          <>
            <Route path="/:orgId/:propertyId/:pageId" element={withSuspense(RedirectToAuth)} />
            <Route path="/:orgId/:propertyId" element={withSuspense(AuthenticationScreen)} />
          </>
        )}

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </RoommateSelectionConfirmationProvider>
  );
}

export default App;
